import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../../utils/toast';
import { listingsService } from '../../api';
import { useAuth } from '../../hooks/useAuth';

// Mirrors mobile Step 5 (sell.tsx Contact). Saves contact preferences then
// publishes the listing (deducting 1 credit). Uses the `submit` action on the
// final step button.
export const useContactHandler = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === 'DEALER';

  const handleBack = useCallback(() => true, []);

  const submitContactAndPublish = useCallback(
    async (formData: Record<string, any>): Promise<boolean> => {
      const required = ['displayPhoneNumber', 'showWhatsApp'];
      for (const f of required) {
        if (!formData[f]) {
          showToast.error(`Please select an option for ${f}`);
          return false;
        }
      }

      if (formData.telephoneNumber) {
        const phoneOk = /^[0-9\s\-()+]{7,20}$/.test(formData.telephoneNumber);
        if (!phoneOk) {
          showToast.error('Please enter a valid phone number');
          return false;
        }
      }

      const params = new URLSearchParams(window.location.search);
      const id = params.get('id') || undefined;

      if (!id) {
        showToast.error('Listing not saved yet');
        return false;
      }

      try {
        await listingsService.upsertStep({
          id,
          step: 4,
          mode: 'update' as const,
          data: {
            telephoneNumber: formData.telephoneNumber
              ? formData.telephoneNumber.startsWith('+') ? formData.telephoneNumber : `+355${formData.telephoneNumber}`
              : null,
            displayPhoneNumber: formData.displayPhoneNumber === 'yes',
            showWhatsApp: formData.showWhatsApp === 'yes',
          },
        });
      } catch (err) {
        console.error('[LISTING][STEP 4] failed', err);
        showToast.error('Failed to save contact info');
        return false;
      }

      // Already-published listings: skip publish, just navigate.
      const alreadyLive = String(formData.__status || '').toUpperCase() === 'LIVE';
      if (alreadyLive) {
        showToast.success('Listing updated');
        navigate(`/listing/${id}`);
        return true;
      }

      try {
        await listingsService.publishWithCredits(id);
        showToast.success('Listing published!');
        navigate(`/listing/${id}`);
        return true;
      } catch (err: any) {
        console.error('[LISTING][PUBLISH] failed', err);
        const msg: string = err?.response?.data?.message || '';

        // Backend already-published guard — treat as a successful update.
        if (/already published/i.test(msg)) {
          showToast.success('Listing updated');
          navigate(`/listing/${id}`);
          return true;
        }

        if (isDealer && (/subscription/i.test(msg) || /active dealer/i.test(msg))) {
          showToast.error('Your dealer subscription is inactive. Please subscribe to publish listings.');
        } else if (!isDealer && (/insufficient credits/i.test(msg) || /credit/i.test(msg))) {
          showToast.error('Insufficient credits. Please purchase credits to publish your listing.');
        } else {
          showToast.error(msg || 'Failed to publish listing');
        }
        return false;
      }
    },
    [navigate, isDealer]
  );

  return {
    handleBack,
    handleNext: submitContactAndPublish,
    handleSubmit: submitContactAndPublish,
  };
};
